import { Box, Checkbox, colors, FormControlLabel, TextField, Typography } from "@mui/material"
import FontAwesomeIcon from "@v2/components/FontAwesomeIcon"
import FlexBox from "@v2/components/styled/FlexBox"
import { uiColors } from "@v2/lib/color"
import { ChangeEvent, useCallback, useImperativeHandle, useMemo, useState } from "react"
import { FixedSizeList as List } from 'react-window'
import { FilterBaseProps, PLPFilterBrand } from "../../types"


export type BrandFilterValueType = null | string[]
export interface BrandFilterProps extends FilterBaseProps<BrandFilterValueType> {
    brands: PLPFilterBrand[],
}

export default function BrandFilter({ ref, searchParamsKey, brands, defaultValue }: BrandFilterProps) {

    const [value, setValue] = useState<BrandFilterValueType>(defaultValue ?? null)
    const [searchText, setSearchText] = useState<string | null>(null)
    const [filteredBrands, setFilteredBrands] = useState<PLPFilterBrand[] | null>(null)
    const brandList = filteredBrands ?? brands

    useImperativeHandle(ref, () => ({
        extract: () => {
            return value;
        },
        apply: (params: URLSearchParams): URLSearchParams => {
            if (value?.length) {
                params.set(searchParamsKey, value.join(','))
            } else {
                params.delete(searchParamsKey)
            }
            setFilteredBrands(null)
            setSearchText(null)
            return params;
        },
        clear: () => setValue(null)
    }), [value])

    const onChangeSearchInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;
        setSearchText(text)

        if (!text) return setFilteredBrands(null)

        setFilteredBrands(() => {
            return brands.filter(brand => {
                const faName = brand.fa_name.toLocaleLowerCase();
                const enName = brand.en_name.toLocaleLowerCase();

                return faName.startsWith(text)
                    || faName.replace(' ', '').startsWith(text)
                    || enName.startsWith(text)
                    || enName.replace(' ', '').startsWith(text)
            })
        })
    }, [brands, value])

    const onSelectItem = useCallback((brand: PLPFilterBrand) => () => {
        const isExists = value?.indexOf(brand.id) > -1;
        setValue(v => isExists
            ? v.filter(i => i !== brand.id)
            : [...(v ?? []), brand.id])
    }, [value])

    const BrandRow = useMemo(() => ({ index, style }) => {
        const brand = brandList[index];
        const isSelected = value?.indexOf(brand.id) > -1
        return (
            <Box style={style} key={brand.id} >
                <FormControlLabel
                    onChange={onSelectItem(brand)}
                    sx={{
                        userSelect: 'none',
                        width: '100%',
                        mr: 0,
                        direction: 'ltr'
                    }} control={<Checkbox disableRipple name="brand" checked={isSelected}
                        icon={
                            <FontAwesomeIcon size={22} color={colors.grey[700]} icon="fa-light fa-square" />
                        } checkedIcon={
                            <FontAwesomeIcon size={22} color={uiColors.primary} icon="fa-light fa-solid fa-square-check" />
                        } />}
                    slotProps={{
                        typography: {
                            component: 'div',
                            sx: {
                                flex: 1
                            }
                        }
                    }}
                    label={
                        <FlexBox sx={{
                            justifyContent: 'space-between'
                        }}>
                            <Typography sx={{
                                fontWeight: 400,
                                color: colors.grey[800]
                            }}> {brand.fa_name} </Typography>
                            <Typography sx={{
                                fontSize: { xs: 14, sm: 16 },
                                fontWeight: 300,
                                color: colors.grey[600]
                            }}>{brand.en_name}</Typography>
                        </FlexBox>
                    } />
            </Box>
        )
    }, [brandList, value])

    return <Box sx={{ width: 1 }}>
        {brands.length > 10 && (
            <TextField value={searchText ?? ""} size="small" variant="outlined" placeholder="جستجو در برند ها" fullWidth onChange={onChangeSearchInput}
                slotProps={{
                    htmlInput: {
                        sx: {
                            fontSize: {
                                xs: 14,
                                sm: 16
                            }
                        }
                    }
                }}

            />
        )}
        <Box sx={{
            mt: brandList.length > 10 ? 1 : 0,
            '& .v_list': {
                overflowY: 'auto',
                scrollbarColor: `${colors.grey[800]} ${colors.grey[200]}`,
                scrollbarWidth: 'thin',
                scrollBehavior: 'smooth'
            }
        }}>
            <List
                className="v_list"
                height={(brandList.length > 6 ? 6 : brandList.length) * 48}
                itemCount={brandList.length}
                itemSize={48}
                width={'100%'}
            >
                {BrandRow}
            </List>
        </Box>
    </Box>
}




